import { useSdk } from "@cs_teamwaydev/mailvista-sdk_react";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { ScrollArea } from "@/components/ui/scroll-area";
import Spinner from "@/components/ui/spinner";
import DisplayAttachment from "@/domain/entities/emails/DisplayAttachment";
import { toast } from "@/hooks/utils/useToast";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../ui/dialog";

interface DocumentPreviewProps {
    attachment: DisplayAttachment;
    icon: ReactNode;
    isOpen: boolean;
    onClose: () => void;
    onPrevious?: () => void;
    onNext?: () => void;
    hasPrevious?: boolean;
    hasNext?: boolean;
}

const DocumentPreview = ({
    attachment,
    icon,
    isOpen,
    onClose,
    onPrevious,
    onNext,
    hasPrevious = false,
    hasNext = false,
}: DocumentPreviewProps) => {
    const { t } = useTranslation();
    const { mailManager } = useSdk();

    // Resolver de preview unifié (cf. AB#1727) :
    // - THREAD      : `previewInline` (data URL) déjà présente → affichage instantané, aucun fetch.
    // - CUSTOM_VIEW : pas de preview → récupérée À LA DEMANDE via mailManager.getAttachmentPreview(ref).
    const [previewUrl, setPreviewUrl] = useState<string | null>(
        attachment.previewInline ?? null,
    );
    const [isResolving, setIsResolving] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const resolvePreview = async () => {
            // THREAD : preview déjà présente (data URL) → instantané, aucun fetch.
            if (attachment.previewInline) {
                setPreviewUrl(attachment.previewInline);

                return;
            }

            // Ni preview inline ni ref résolvable → rien à afficher (« aperçu indisponible »).
            if (!attachment.ref) {
                setPreviewUrl(null);

                return;
            }

            // CUSTOM_VIEW : preview récupérée À LA DEMANDE (best-effort ; `null` → aperçu indisponible).
            setIsResolving(true);
            try {
                const url = await mailManager.getAttachmentPreview(
                    attachment.ref,
                );
                if (!cancelled) {
                    setPreviewUrl(url);
                }
            } catch {
                if (!cancelled) {
                    setPreviewUrl(null);
                }
            } finally {
                if (!cancelled) {
                    setIsResolving(false);
                }
            }
        };

        resolvePreview();

        return () => {
            cancelled = true;
        };
    }, [attachment, mailManager]);

    const downloadFile = async () => {
        if (!previewUrl) {
            toast({
                title: t("errors.downloadDocument"),
                description: t("errors.global.description"),
                variant: "error",
            });

            return;
        }

        try {
            const response = await fetch(previewUrl);
            const blob = await response.blob();
            const typedBlob = attachment.type
                ? new Blob([blob], { type: attachment.type })
                : blob;
            const downloadUrl = window.URL.createObjectURL(typedBlob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = attachment.getDownloadName();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error("Error while downloading file:", error);
            toast({
                title: t("errors.downloadDocument"),
                description: t("errors.global.description"),
                variant: "error",
            });
        }
    };

    const renderPreview = () => {
        if (isResolving) {
            return (
                <div className="flex flex-1 items-center justify-center">
                    <Spinner size="large" />
                </div>
            );
        }

        if (previewUrl && attachment.isImage()) {
            return (
                <ScrollArea className="flex-1">
                    <div className="flex h-full w-full items-center justify-center">
                        <img
                            alt={attachment.name}
                            className="max-h-full max-w-full object-contain"
                            src={previewUrl}
                        />
                    </div>
                </ScrollArea>
            );
        }

        // Le Viewer gère son propre défilement : pas de ScrollArea pour éviter
        // une double barre de défilement verticale.
        if (previewUrl && attachment.isPdf()) {
            return (
                <div className="min-h-0 flex-1">
                    <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                        <Viewer fileUrl={previewUrl} />
                    </Worker>
                </div>
            );
        }

        return (
            <ScrollArea className="flex-1">
                <div className="flex h-full w-full flex-col items-center justify-center gap-4">
                    <p className="text-text-main text-lg">
                        {t("widgets.documents.previewNotAvailable")}
                    </p>
                    <div className="flex gap-2">
                        <Button
                            className="flex items-center justify-center gap-2"
                            disabled={!previewUrl}
                            onClick={downloadFile}
                            variant="outline"
                        >
                            <Download className="h-5 w-5" />
                            {t("widgets.documents.download")}
                        </Button>
                    </div>
                </div>
            </ScrollArea>
        );
    };

    return (
        <Dialog onOpenChange={onClose} open={isOpen}>
            <DialogContent variant="full">
                <DialogHeader className="relative flex flex-row items-center">
                    <DialogTitle className="flex items-center gap-2">
                        {icon}
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                            {attachment.name}
                        </span>
                    </DialogTitle>
                    <div className="absolute left-1/2 flex -translate-x-1/2 items-center justify-center gap-2">
                        <div className="flex items-center gap-2">
                            <Button
                                disabled={!hasPrevious}
                                onClick={onPrevious}
                                size="smallCircle"
                                variant="ghost"
                            >
                                <ChevronLeft />
                            </Button>
                            <Button
                                disabled={!hasNext}
                                onClick={onNext}
                                size="smallCircle"
                                variant="ghost"
                            >
                                <ChevronRight />
                            </Button>
                        </div>
                        <button
                            className="ml-5 flex h-8 w-8 items-center justify-center rounded-full hover:bg-icon-base"
                            onClick={downloadFile}
                            type="button"
                        >
                            <Download className="h-5 w-5" />
                        </button>
                    </div>
                </DialogHeader>
                {renderPreview()}
            </DialogContent>
        </Dialog>
    );
};

export default DocumentPreview;
